import { createAccountId, createFirmId, createLawyerId } from '@domain/value-objects';
import type { Unbrand } from '@shared/value-objects/brand';
import { optVO, reqRecord, reqVO, voArray } from '@shared/value-objects/parse-helpers';
import {
  createEmail,
  createISODate,
  createMoneyAmount,
  createPostalCode,
} from '@shared/value-objects';
import type { Contract, ContractPartyBrief } from './model';

/// API の生レスポンス (`unknown`) を検証して branded な `Contract` にする門番 (parse, don't validate / #471)。
/// 失敗方針: 必須 id・present かつ不正な任意フィールドは throw、任意の値なしは null、`raw` 値はログに載せない。

/// U2 (ADR 20260812-contract-party-separation) 以前の backend は `party` を
/// 応答に持たない。rollout 安全性 (新 frontend + 旧 backend のデプロイ窓で契約
/// 画面が全滅しないこと) のため、`party` 欠落は throw せず none 相当へ
/// フォールバックする (2026-08-25 PR #832 レビュー対応)。U2 以前は個人契約表示
/// 自体が存在しなかったため、この窓でバッジが出ないのは従来どおりの画面であり
/// 退行ではない。フォールバックは **false 固定** — 旧 `is_individual_contract`
/// フラグは読まない (server 導出値のみを読み、判定を複製しない方針を維持)。
/// `party` が present のときの検証 (individual_lawyer_id の UUID 不正等) は
/// 従来どおり throw する。
function parseContractParty(raw: unknown): ContractPartyBrief {
  if (raw === null || raw === undefined) {
    return {
      party_kind: null,
      individual_lawyer_id: null,
      individual_lawyer_name: null,
      is_individual_display: false,
    };
  }
  reqRecord(raw, 'contract.party');
  const partyDto = raw as Unbrand<ContractPartyBrief>;
  return {
    ...partyDto,
    individual_lawyer_id: optVO(
      partyDto.individual_lawyer_id,
      createLawyerId,
      'contract.party.individual_lawyer_id',
    ),
  };
}

export function parseContract(raw: unknown): Contract {
  reqRecord(raw, 'contract');
  const dto = raw as Unbrand<Contract>;
  return {
    ...dto,
    firm_id: reqVO(dto.firm_id, createFirmId, 'contract.firm_id'),
    assignee_account_ids: voArray(
      dto.assignee_account_ids,
      createAccountId,
      'contract.assignee_account_ids',
    ),
    primary_lawyer_id: optVO(dto.primary_lawyer_id, createLawyerId, 'contract.primary_lawyer_id'),
    related_lawyer_ids: voArray(
      dto.related_lawyer_ids,
      createLawyerId,
      'contract.related_lawyer_ids',
    ),
    main_interest_lawyer_id: optVO(
      dto.main_interest_lawyer_id,
      createLawyerId,
      'contract.main_interest_lawyer_id',
    ),
    archived_by_account_id: optVO(
      dto.archived_by_account_id,
      createAccountId,
      'contract.archived_by_account_id',
    ),
    monthly_amount: optVO(dto.monthly_amount, createMoneyAmount, 'contract.monthly_amount'),
    mrr_manual: optVO(dto.mrr_manual, createMoneyAmount, 'contract.mrr_manual'),
    initial_amount: optVO(dto.initial_amount, createMoneyAmount, 'contract.initial_amount'),
    annual_contract_amount: optVO(
      dto.annual_contract_amount,
      createMoneyAmount,
      'contract.annual_contract_amount',
    ),
    proposed_acv: optVO(dto.proposed_acv, createMoneyAmount, 'contract.proposed_acv'),
    proposed_mrr: optVO(dto.proposed_mrr, createMoneyAmount, 'contract.proposed_mrr'),
    spot_amount: optVO(dto.spot_amount, createMoneyAmount, 'contract.spot_amount'),
    signed_on: optVO(dto.signed_on, createISODate, 'contract.signed_on'),
    starts_on: optVO(dto.starts_on, createISODate, 'contract.starts_on'),
    ends_on: optVO(dto.ends_on, createISODate, 'contract.ends_on'),
    mrr_recognition_start_month: optVO(
      dto.mrr_recognition_start_month,
      createISODate,
      'contract.mrr_recognition_start_month',
    ),
    mrr_recognition_end_month: optVO(
      dto.mrr_recognition_end_month,
      createISODate,
      'contract.mrr_recognition_end_month',
    ),
    canceled_on: optVO(dto.canceled_on, createISODate, 'contract.canceled_on'),
    billing_email: optVO(dto.billing_email, createEmail, 'contract.billing_email'),
    pro_billing_email: optVO(dto.pro_billing_email, createEmail, 'contract.pro_billing_email'),
    pro_billing_postal_code: optVO(
      dto.pro_billing_postal_code,
      createPostalCode,
      'contract.pro_billing_postal_code',
    ),
    party: parseContractParty(dto.party),
  };
}
